// Support ticket page, user-facing
// /suporte, list user's tickets and open new ones

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/ToastSystem';
import { EmptyState } from '../components/ui/EmptyState';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';

interface Ticket {
  id: string;
  subject: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface TicketMessage {
  id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  open:             'Aberto',
  in_progress:      'Em andamento',
  waiting_customer: 'Aguardando você',
  resolved:         'Resolvido',
  closed:           'Fechado',
};

const STATUS_COLORS: Record<string, string> = {
  open:             '#60a5fa',
  in_progress:      '#f59e0b',
  waiting_customer: '#c084fc',
  resolved:         '#4ade80',
  closed:           'var(--text-muted)',
};

export function SupportPage() {
  const { profile }       = useAuth();
  const { showToast }     = useToast();
  const [tickets,         setTickets]        = useState<Ticket[]>([]);
  const [loading,         setLoading]        = useState(true);
  const [expanded,        setExpanded]       = useState<string | null>(null);
  const [messages,        setMessages]       = useState<Record<string, TicketMessage[]>>({});
  const [newMessage,      setNewMessage]     = useState('');
  const [sendingMsg,      setSendingMsg]     = useState(false);
  const [showNewTicket,   setShowNewTicket]  = useState(false);
  const [newSubject,      setNewSubject]     = useState('');
  const [newBody,         setNewBody]        = useState('');
  const [creating,        setCreating]       = useState(false);

  useEffect(() => {
    if (profile?.id) loadTickets();
  }, [profile?.id]);

  async function loadTickets() {
    setLoading(true);
    const { data } = await supabase
      .from('support_tickets')
      .select('id, subject, status, priority, created_at, updated_at')
      .eq('user_id', profile!.id)
      .order('updated_at', { ascending: false });
    setTickets((data ?? []) as Ticket[]);
    setLoading(false);
  }

  async function loadMessages(ticketId: string) {
    if (messages[ticketId]) return;
    const { data } = await supabase
      .from('ticket_messages')
      .select('id, sender_type, message, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });
    setMessages(prev => ({ ...prev, [ticketId]: (data ?? []) as TicketMessage[] }));
  }

  async function toggleExpand(ticketId: string) {
    if (expanded === ticketId) {
      setExpanded(null);
    } else {
      setExpanded(ticketId);
      await loadMessages(ticketId);
    }
  }

  async function sendMessage(ticketId: string) {
    if (!newMessage.trim()) return;
    setSendingMsg(true);
    const { error } = await supabase.from('ticket_messages').insert({
      ticket_id: ticketId, sender_type: 'user', message: newMessage.trim(),
    });
    setSendingMsg(false);
    if (error) { showToast({ type: 'error', message: 'Erro ao enviar mensagem.' }); return; }
    setNewMessage('');
    // Refresh messages
    const { data } = await supabase
      .from('ticket_messages').select('id, sender_type, message, created_at')
      .eq('ticket_id', ticketId).order('created_at', { ascending: true });
    setMessages(prev => ({ ...prev, [ticketId]: (data ?? []) as TicketMessage[] }));
  }

  async function createTicket() {
    if (!newSubject.trim() || !newBody.trim()) return;
    setCreating(true);
    const { data: ticket, error: ticketErr } = await supabase
      .from('support_tickets')
      .insert({ user_id: profile!.id, subject: newSubject.trim() })
      .select('id').single();
    if (ticketErr || !ticket) {
      showToast({ type: 'error', message: 'Erro ao criar ticket.' });
      setCreating(false); return;
    }
    await supabase.from('ticket_messages').insert({
      ticket_id: ticket.id, sender_type: 'user', message: newBody.trim(),
    });
    setCreating(false);
    setShowNewTicket(false);
    setNewSubject(''); setNewBody('');
    showToast({ type: 'success', message: 'Ticket criado! Responderemos em breve ✦' });
    loadTickets();
  }

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
            Suporte
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
            Abra um ticket e nossa equipe responderá em até 24h.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          onClick={() => setShowNewTicket(true)}>
          <Plus size={15} /> Novo ticket
        </button>
      </div>

      {/* New ticket modal */}
      {showNewTicket && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '480px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: 0 }}>Abrir novo ticket</h2>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowNewTicket(false)} aria-label="Fechar"><X size={16} /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Assunto</label>
              <input type="text" className="form-input" value={newSubject} onChange={e => setNewSubject(e.target.value)}
                placeholder="Ex: Problema com pagamento" maxLength={200} />
            </div>
            <div className="form-group">
              <label className="form-label">Descrição detalhada</label>
              <textarea className="form-input" value={newBody} onChange={e => setNewBody(e.target.value)}
                placeholder="Descreva seu problema com o máximo de detalhes possível..."
                rows={5} style={{ resize: 'vertical', minHeight: '120px' }} maxLength={2000} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={createTicket}
                disabled={creating || newSubject.trim().length < 3 || newBody.trim().length < 10}>
                {creating ? 'Criando...' : 'Enviar ticket'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowNewTicket(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[1,2,3].map(i => <div key={i} className="card shimmer" style={{ height: '64px' }} />)}
        </div>
      )}

      {!loading && tickets.length === 0 && (
        <EmptyState
          emoji="🎧"
          title="Nenhum ticket aberto"
          subtitle="Abra um ticket e nossa equipe responderá em até 24 horas úteis."
          cta="Abrir primeiro ticket"
          ctaAction={() => setShowNewTicket(true)}
        />
      )}

      {!loading && tickets.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tickets.map(ticket => (
            <div key={ticket.id} className="card" style={{ padding: 0 }}>
              {/* Header */}
              <button style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}
                onClick={() => toggleExpand(ticket.id)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{
                      fontSize: '10px', padding: '1px 8px', borderRadius: '999px', fontWeight: 700,
                      backgroundColor: `${STATUS_COLORS[ticket.status]}18`,
                      color: STATUS_COLORS[ticket.status],
                    }}>
                      {STATUS_LABELS[ticket.status] ?? ticket.status}
                    </span>
                    {ticket.status === 'waiting_customer' && (
                      <span style={{ fontSize: '10px', color: '#c084fc', fontWeight: 700 }}>● Aguarda resposta sua</span>
                    )}
                  </div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.15rem', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.subject}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                    Atualizado {new Date(ticket.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                {expanded === ticket.id
                  ? <ChevronUp size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  : <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
              </button>

              {/* Expanded: messages + reply */}
              {expanded === ticket.id && (
                <div className="animate-fade-in" style={{ borderTop: '1px solid var(--border-subtle)', padding: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    {(messages[ticket.id] ?? []).map(msg => (
                      <div key={msg.id} style={{
                        padding: '0.75rem 1rem',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: msg.sender_type === 'admin'
                          ? 'rgba(31,78,95,0.06)' : 'var(--bg-elevated)',
                        borderLeft: msg.sender_type === 'admin'
                          ? '3px solid var(--petrol)' : '3px solid var(--border-subtle)',
                      }}>
                        <p style={{ fontSize: 'var(--text-caption)', color: msg.sender_type === 'admin' ? 'var(--petrol)' : 'var(--text-muted)', fontWeight: 600, marginBottom: '0.35rem' }}>
                          {msg.sender_type === 'admin' ? '✦ Equipe Professya' : 'Você'} · {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {msg.message}
                        </p>
                      </div>
                    ))}
                  </div>

                  {!['resolved','closed'].includes(ticket.status) && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <textarea
                        className="form-input"
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        placeholder="Adicionar resposta..."
                        rows={2}
                        style={{ flex: 1, resize: 'vertical', minHeight: '60px', fontSize: 'var(--text-label)' }}
                        maxLength={2000}
                      />
                      <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end', flexShrink: 0 }}
                        onClick={() => sendMessage(ticket.id)}
                        disabled={sendingMsg || !newMessage.trim()}>
                        {sendingMsg ? '...' : 'Enviar'}
                      </button>
                    </div>
                  )}
                  {['resolved','closed'].includes(ticket.status) && (
                    <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Este ticket está {STATUS_LABELS[ticket.status].toLowerCase()}.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
